import { useEffect, useRef } from 'react';
import type { ReactElement } from 'react';
import { flakeCount, snowFrameDue } from '../../lib/christmas';
import './christmas.css';
import { useSnowOn } from './useChristmas';

interface Flake {
  x: number;
  y: number;
  v: number;
  d: number;
  sprite: number;
}

const SIZES = [1, 2, 3];
const ALPHAS = [0.55, 0.7, 0.85];

/** One small round flake with a faint cool halo, drawn once and reused. */
function makeSprite(rgb: string, r: number, alpha: number): HTMLCanvasElement {
  const size = Math.ceil(r * 2) + 4;
  const sprite = document.createElement('canvas');
  sprite.width = size;
  sprite.height = size;
  const pen = sprite.getContext('2d');
  if (pen) {
    pen.fillStyle = 'rgba(90, 110, 140, 0.2)';
    pen.beginPath();
    pen.arc(size / 2, size / 2, r + 1, 0, 6.283);
    pen.fill();
    pen.fillStyle = `rgba(${rgb}, ${alpha})`;
    pen.beginPath();
    pen.arc(size / 2, size / 2, r, 0, 6.283);
    pen.fill();
  }
  return sprite;
}

/**
 * Small, light snow over the whole app. A fixed canvas that ignores the
 * pointer, with a capped flake count. Cheap on purpose: flakes are drawn from
 * a few pre-rendered sprites (no shadows, no per-frame colour strings), at
 * most 30 times a second, at pixel ratio 1, and not at all while the tab is
 * hidden or the window is not focused. Not rendered at all out of season,
 * when switched off, or under prefers-reduced-motion (useSnowOn decides).
 */
export function SnowCanvas(): ReactElement | null {
  const on = useSnowOn();
  const ref = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = ref.current;
    const context = canvas?.getContext('2d');
    if (!on || !canvas || !context) return undefined;

    const root = document.documentElement;
    let flakes: Flake[] = [];
    let sprites: HTMLCanvasElement[] = [];
    let frame = 0;
    let last: number | null = null;
    let rgb = '255, 255, 255';

    const readInk = () => {
      rgb = getComputedStyle(root).getPropertyValue('--snow').trim() || rgb;
      sprites = SIZES.flatMap((r) => ALPHAS.map((a) => makeSprite(rgb, r, a)));
    };
    const build = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      canvas.width = width;
      canvas.height = height;
      flakes = Array.from({ length: flakeCount(width) }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        v: 0.25 + Math.random() * 0.55,
        d: Math.random() * 6.28,
        sprite: Math.floor(Math.random() * SIZES.length * ALPHAS.length),
      }));
    };
    const draw = (now: number) => {
      frame = requestAnimationFrame(draw);
      if (!snowFrameDue(now, last)) return;
      // Move by real time, so the fall speed matches the old 60 a second.
      const step = last === null ? 1 : Math.min((now - last) / 16.7, 4);
      last = now;
      const width = window.innerWidth;
      const height = window.innerHeight;
      context.clearRect(0, 0, width, height);
      for (const flake of flakes) {
        flake.y += flake.v * step;
        flake.d += 0.01 * step;
        flake.x += Math.sin(flake.d) * 0.3 * step;
        if (flake.y > height + 4) {
          flake.y = -4;
          flake.x = Math.random() * width;
        }
        const sprite = sprites[flake.sprite];
        if (sprite) {
          context.drawImage(
            sprite,
            Math.round(flake.x - sprite.width / 2),
            Math.round(flake.y - sprite.height / 2),
          );
        }
      }
    };
    const shouldRun = () => !document.hidden && document.hasFocus();
    const start = () => {
      if (frame === 0 && shouldRun()) {
        last = null;
        frame = requestAnimationFrame(draw);
      }
    };
    const stop = () => {
      cancelAnimationFrame(frame);
      frame = 0;
    };
    const onVisibility = () => {
      if (document.hidden) stop();
      else start();
    };
    const onResize = () => {
      build();
    };

    readInk();
    build();
    start();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('blur', stop);
    window.addEventListener('focus', start);
    window.addEventListener('resize', onResize);
    // Switching theme changes --snow (light page: grey-blue, dark: white).
    const watcher = new MutationObserver(readInk);
    watcher.observe(root, { attributes: true, attributeFilter: ['data-theme'] });
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('blur', stop);
      window.removeEventListener('focus', start);
      window.removeEventListener('resize', onResize);
      watcher.disconnect();
    };
  }, [on]);

  if (!on) return null;
  return <canvas ref={ref} className="xmas-snow" aria-hidden="true" data-xmas="snow" />;
}
